import type { Language, Locale } from '@/config/locales';
import type { TaxInvoiceTranslations } from './types';
import { PRICING } from '@/constants/pricing';
import { DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';

// TAX_RATE는 PRICING.DEFAULT_TAX_RATE로 대체
export const TAX_RATE = PRICING.DEFAULT_TAX_RATE;

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const translations:
  & Record<Language, TaxInvoiceTranslations>
  & Record<Exclude<Locale, Language>, TaxInvoiceTranslations> = {
  ko: {
    taxInvoice: '세금계산서 발급',
    cfdiInvoice: 'CFDI 전자세금계산서',
    selectCompany: '회사 선택',
    selectedCompany: '선택된 회사',
    orderInfo: '주문 정보',
    orderNumber: '주문 번호',
    amount: '금액',
    date: '날짜',
    generateInvoice: '세금계산서 발급',
    generating: '발급 중...',
    cancel: '취소',
    changeCompany: '회사 변경',
    invoiceDetails: '세금계산서 상세',
    taxRate: '세율',
    beforeTax: '세전 금액',
    taxAmount: '세액',
    totalAmount: '총 금액'
  },
  es: {
    taxInvoice: 'Factura Fiscal',
    cfdiInvoice: 'Comprobante Fiscal Digital por Internet (CFDI)',
    selectCompany: 'Seleccionar empresa',
    selectedCompany: 'Empresa seleccionada',
    orderInfo: 'Información del pedido',
    orderNumber: 'Número de pedido',
    amount: 'Monto',
    date: 'Fecha',
    generateInvoice: 'Generar factura',
    generating: 'Generando...',
    cancel: 'Cancelar',
    changeCompany: 'Cambiar empresa',
    invoiceDetails: 'Detalles de la factura',
    taxRate: 'Tasa de IVA',
    beforeTax: 'Subtotal',
    taxAmount: 'IVA',
    totalAmount: 'Total'
  },
  en: {
    taxInvoice: 'Tax Invoice',
    cfdiInvoice: 'Digital Tax Receipt (CFDI)',
    selectCompany: 'Select company',
    selectedCompany: 'Selected company',
    orderInfo: 'Order information',
    orderNumber: 'Order number',
    amount: 'Amount',
    date: 'Date',
    generateInvoice: 'Generate invoice',
    generating: 'Generating...',
    cancel: 'Cancel',
    changeCompany: 'Change company',
    invoiceDetails: 'Invoice details',
    taxRate: 'Tax rate',
    beforeTax: 'Subtotal',
    taxAmount: 'Tax',
    totalAmount: 'Total'
  },
  pt: {
    taxInvoice: 'Fatura fiscal',
    cfdiInvoice: 'Comprovante Fiscal Digital (CFDI)',
    selectCompany: 'Selecionar empresa',
    selectedCompany: 'Empresa selecionada',
    orderInfo: 'Informações do pedido',
    orderNumber: 'Número do pedido',
    amount: 'Valor',
    date: 'Data',
    generateInvoice: 'Gerar fatura',
    generating: 'Gerando...',
    cancel: 'Cancelar',
    changeCompany: 'Alterar empresa',
    invoiceDetails: 'Detalhes da fatura',
    taxRate: 'Taxa de IVA',
    beforeTax: 'Subtotal',
    taxAmount: 'IVA',
    totalAmount: 'Total'
  },
  fr: {
    taxInvoice: 'Facture fiscale',
    cfdiInvoice: 'Reçu fiscal numérique (CFDI)',
    selectCompany: 'Sélectionner une entreprise',
    selectedCompany: 'Entreprise sélectionnée',
    orderInfo: 'Informations de la commande',
    orderNumber: 'Numéro de commande',
    amount: 'Montant',
    date: 'Date',
    generateInvoice: 'Générer la facture',
    generating: 'Génération...',
    cancel: 'Annuler',
    changeCompany: "Changer d'entreprise",
    invoiceDetails: 'Détails de la facture',
    taxRate: 'Taux de taxe',
    beforeTax: 'Sous-total',
    taxAmount: 'Taxe',
    totalAmount: 'Total'
  },
  zh: {
    taxInvoice: '税务发票',
    cfdiInvoice: '电子税务发票 (CFDI)',
    selectCompany: '选择公司',
    selectedCompany: '已选公司',
    orderInfo: '订单信息',
    orderNumber: '订单号',
    amount: '金额',
    date: '日期',
    generateInvoice: '开具发票',
    generating: '开具中...',
    cancel: '取消',
    changeCompany: '更换公司',
    invoiceDetails: '发票详情',
    taxRate: '税率',
    beforeTax: '小计',
    taxAmount: '税额',
    totalAmount: '总计'
  },
  ja: {
    taxInvoice: '税務請求書',
    cfdiInvoice: '電子税務領収書 (CFDI)',
    selectCompany: '会社を選択',
    selectedCompany: '選択された会社',
    orderInfo: '注文情報',
    orderNumber: '注文番号',
    amount: '金額',
    date: '日付',
    generateInvoice: '請求書を発行',
    generating: '発行中...',
    cancel: 'キャンセル',
    changeCompany: '会社を変更',
    invoiceDetails: '請求書の詳細',
    taxRate: '税率',
    beforeTax: '小計',
    taxAmount: '税額',
    totalAmount: '合計'
  },
  hi: {
    taxInvoice: 'टैक्स इनवॉइस',
    cfdiInvoice: 'डिजिटल टैक्स रसीद (CFDI)',
    selectCompany: 'कंपनी चुनें',
    selectedCompany: 'चयनित कंपनी',
    orderInfo: 'ऑर्डर जानकारी',
    orderNumber: 'ऑर्डर नंबर',
    amount: 'राशि',
    date: 'तारीख',
    generateInvoice: 'इनवॉइस जारी करें',
    generating: 'जारी हो रहा है...',
    cancel: 'रद्द करें',
    changeCompany: 'कंपनी बदलें',
    invoiceDetails: 'इनवॉइस विवरण',
    taxRate: 'कर दर',
    beforeTax: 'उप-योग',
    taxAmount: 'कर राशि',
    totalAmount: 'कुल'
  },
  de: {
    taxInvoice: 'Steuerrechnung',
    cfdiInvoice: 'Digitaler Steuerbeleg (CFDI)',
    selectCompany: 'Unternehmen auswählen',
    selectedCompany: 'Ausgewähltes Unternehmen',
    orderInfo: 'Bestellinformationen',
    orderNumber: 'Bestellnummer',
    amount: 'Betrag',
    date: 'Datum',
    generateInvoice: 'Rechnung erstellen',
    generating: 'Wird erstellt...',
    cancel: 'Abbrechen',
    changeCompany: 'Unternehmen ändern',
    invoiceDetails: 'Rechnungsdetails',
    taxRate: 'Steuersatz',
    beforeTax: 'Zwischensumme',
    taxAmount: 'Steuerbetrag',
    totalAmount: 'Gesamt'
  },
  it: {
    taxInvoice: 'Fattura fiscale',
    cfdiInvoice: 'Ricevuta fiscale digitale (CFDI)',
    selectCompany: 'Seleziona azienda',
    selectedCompany: 'Azienda selezionata',
    orderInfo: "Informazioni sull'ordine",
    orderNumber: 'Numero ordine',
    amount: 'Importo',
    date: 'Data',
    generateInvoice: 'Genera fattura',
    generating: 'Generazione...',
    cancel: 'Annulla',
    changeCompany: 'Cambia azienda',
    invoiceDetails: 'Dettagli della fattura',
    taxRate: 'Aliquota IVA',
    beforeTax: 'Subtotale',
    taxAmount: 'IVA',
    totalAmount: 'Totale'
  },
  ru: {
    taxInvoice: 'Счёт-фактура',
    cfdiInvoice: 'Цифровой налоговый чек (CFDI)',
    selectCompany: 'Выбрать компанию',
    selectedCompany: 'Выбранная компания',
    orderInfo: 'Информация о заказе',
    orderNumber: 'Номер заказа',
    amount: 'Сумма',
    date: 'Дата',
    generateInvoice: 'Сформировать счёт',
    generating: 'Формирование...',
    cancel: 'Отмена',
    changeCompany: 'Сменить компанию',
    invoiceDetails: 'Детали счёта',
    taxRate: 'Налоговая ставка',
    beforeTax: 'Подытог',
    taxAmount: 'Сумма налога',
    totalAmount: 'Итого'
  },
  ar: {
    taxInvoice: 'فاتورة ضريبية',
    cfdiInvoice: 'إيصال ضريبي رقمي (CFDI)',
    selectCompany: 'اختيار الشركة',
    selectedCompany: 'الشركة المختارة',
    orderInfo: 'معلومات الطلب',
    orderNumber: 'رقم الطلب',
    amount: 'المبلغ',
    date: 'التاريخ',
    generateInvoice: 'إصدار الفاتورة',
    generating: 'جارٍ الإصدار...',
    cancel: 'إلغاء',
    changeCompany: 'تغيير الشركة',
    invoiceDetails: 'تفاصيل الفاتورة',
    taxRate: 'معدل الضريبة',
    beforeTax: 'المجموع الفرعي',
    taxAmount: 'مبلغ الضريبة',
    totalAmount: 'الإجمالي'
  },
  vi: {
    taxInvoice: 'Hóa đơn thuế',
    cfdiInvoice: 'Hóa đơn thuế điện tử (CFDI)',
    selectCompany: 'Chọn công ty',
    selectedCompany: 'Công ty đã chọn',
    orderInfo: 'Thông tin đơn hàng',
    orderNumber: 'Số đơn hàng',
    amount: 'Số tiền',
    date: 'Ngày',
    generateInvoice: 'Xuất hóa đơn',
    generating: 'Đang tạo...',
    cancel: 'Hủy',
    changeCompany: 'Đổi công ty',
    invoiceDetails: 'Chi tiết hóa đơn',
    taxRate: 'Thuế suất',
    beforeTax: 'Tạm tính',
    taxAmount: 'Tiền thuế',
    totalAmount: 'Tổng cộng'
  },
  th: {
    taxInvoice: 'ใบกำกับภาษี',
    cfdiInvoice: 'ใบกำกับภาษีอิเล็กทรอนิกส์ (CFDI)',
    selectCompany: 'เลือกบริษัท',
    selectedCompany: 'บริษัทที่เลือก',
    orderInfo: 'ข้อมูลคำสั่งซื้อ',
    orderNumber: 'หมายเลขคำสั่งซื้อ',
    amount: 'จำนวนเงิน',
    date: 'วันที่',
    generateInvoice: 'ออกใบกำกับภาษี',
    generating: 'กำลังสร้าง...',
    cancel: 'ยกเลิก',
    changeCompany: 'เปลี่ยนบริษัท',
    invoiceDetails: 'รายละเอียดใบกำกับภาษี',
    taxRate: 'อัตราภาษี',
    beforeTax: 'ยอดรวมย่อย',
    taxAmount: 'จำนวนภาษี',
    totalAmount: 'ยอดรวม'
  },
  nl: {
    taxInvoice: 'Belastingfactuur',
    cfdiInvoice: 'Digitale belastingbon (CFDI)',
    selectCompany: 'Bedrijf selecteren',
    selectedCompany: 'Geselecteerd bedrijf',
    orderInfo: 'Bestelinformatie',
    orderNumber: 'Bestelnummer',
    amount: 'Bedrag',
    date: 'Datum',
    generateInvoice: 'Factuur genereren',
    generating: 'Genereren...',
    cancel: 'Annuleren',
    changeCompany: 'Bedrijf wijzigen',
    invoiceDetails: 'Factuurgegevens',
    taxRate: 'Belastingtarief',
    beforeTax: 'Subtotaal',
    taxAmount: 'Belastingbedrag',
    totalAmount: 'Totaal'
  }
};

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const loadingMessages:
  & Record<Language, { title: string; subtitle: string }>
  & Record<Exclude<Locale, Language>, { title: string; subtitle: string }> = {
  ko: {
    title: 'CFDI 전자세금계산서를 생성하고 있습니다...',
    subtitle: '이 과정은 몇 초 정도 소요됩니다.'
  },
  es: {
    title: 'Generando CFDI electrónico...',
    subtitle: 'Este proceso puede tardar unos segundos.'
  },
  en: {
    title: 'Generating electronic CFDI...',
    subtitle: 'This process may take a few seconds.'
  },
  pt: {
    title: 'Gerando CFDI eletrônico...',
    subtitle: 'Este processo pode levar alguns segundos.'
  },
  fr: {
    title: 'Génération du CFDI électronique...',
    subtitle: 'Ce processus peut prendre quelques secondes.'
  },
  zh: {
    title: '正在生成电子 CFDI...',
    subtitle: '此过程可能需要几秒钟。'
  },
  ja: {
    title: '電子 CFDI を生成しています...',
    subtitle: 'この処理には数秒かかる場合があります。'
  },
  hi: {
    title: 'इलेक्ट्रॉनिक CFDI बनाया जा रहा है...',
    subtitle: 'इस प्रक्रिया में कुछ सेकंड लग सकते हैं।'
  },
  de: {
    title: 'Elektronisches CFDI wird erstellt...',
    subtitle: 'Dieser Vorgang kann einige Sekunden dauern.'
  },
  it: {
    title: 'Generazione del CFDI elettronico...',
    subtitle: 'Questo processo può richiedere alcuni secondi.'
  },
  ru: {
    title: 'Формируется электронный CFDI...',
    subtitle: 'Этот процесс может занять несколько секунд.'
  },
  ar: {
    title: 'جارٍ إنشاء CFDI الإلكتروني...',
    subtitle: 'قد تستغرق هذه العملية بضع ثوانٍ.'
  },
  vi: {
    title: 'Đang tạo CFDI điện tử...',
    subtitle: 'Quá trình này có thể mất vài giây.'
  },
  th: {
    title: 'กำลังสร้าง CFDI อิเล็กทรอนิกส์...',
    subtitle: 'ขั้นตอนนี้อาจใช้เวลาสักครู่'
  },
  nl: {
    title: 'Elektronische CFDI wordt gegenereerd...',
    subtitle: 'Dit proces kan enkele seconden duren.'
  }
};

// formatCurrency는 @/utils/currency의 함수를 사용하세요
// 이 함수는 향후 버전에서 제거될 예정입니다

// #522: Language(3) → Locale(15) 전수 지원 — BCP-47 매핑 (기존 ko-KR/es-MX/en-US 값 보존).
export function formatDate(date: Date, locale: Locale): string {
  const localeMap:
    & Record<Language, string>
    & Record<Exclude<Locale, Language>, string> = {
    ko: 'ko-KR',
    es: 'es-MX',
    en: 'en-US',
    pt: 'pt-BR',
    fr: 'fr-FR',
    zh: 'zh-CN',
    ja: 'ja-JP',
    hi: 'hi-IN',
    de: 'de-DE',
    it: 'it-IT',
    ru: 'ru-RU',
    ar: 'ar-SA',
    vi: 'vi-VN',
    th: 'th-TH',
    nl: 'nl-NL'
  };
  // #1816: 세금계산서(법적 산출물) 날짜도 매장 타임존(멕시코)으로 고정.
  return new Intl.DateTimeFormat(localeMap[locale] ?? localeMap.es, { timeZone: DEFAULT_STORE_TIMEZONE }).format(date);
}
